import { GameCard } from "modules/cards/model/card";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { QUERIES } from "modules/common/query";
import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { API_ROUTES } from "utils/api-route.constants";
import { STATIC_DATA_CONFIG } from "modules/common/react-query-configs";
import {
  appendPricingParams,
  usePricingRequest,
  withoutPrice,
  withPricingCacheKey,
} from "modules/common/hooks/usePricingRequest";
import type { GameId, PricingConfiguration } from "modules/common/model/models";

interface UseCardByDataIdProps {
  dataId: string;
  /** Undefined only while a host modal has no active card. */
  game?: GameId;
  enabled?: boolean;
  pricing?: PricingConfiguration;
}

export const useCardByDataId = ({
  dataId,
  game,
  enabled = true,
  pricing: explicitPricing,
}: UseCardByDataIdProps) => {
  const fetch = useFetchWrapper();
  const queryClient = useQueryClient();
  const { pricing, cacheKey, isAuthReady, shouldMaskCachedPricing } =
    usePricingRequest(explicitPricing);
  const baseQueryKey = [QUERIES.MAGIC_CARDS_DETAILS, game, dataId] as const;

  const query = useQuery({
    queryKey: withPricingCacheKey(baseQueryKey, cacheKey),
    queryFn: () => {
      if (!game) throw new Error(`Card ${dataId} query is missing its game`);
      return fetchCardByDataId(dataId, game);
    },
    enabled: isAuthReady && enabled && !!dataId && !!game,
    placeholderData: () => {
      const cached = queryClient.getQueryData<GameCard>(baseQueryKey);
      return cacheKey && cached ? withoutPrice(cached) : cached;
    },
    ...STATIC_DATA_CONFIG, // 30min stale, no refetch - perfect for static card data
  });

  return {
    query,
    card:
      shouldMaskCachedPricing && query.data
        ? withoutPrice(query.data)
        : query.data,
    isFetching: query.isFetching,
    error: query.error,
  };

  async function fetchCardByDataId(
    dataId: string,
    expectedGame: GameId
  ): Promise<GameCard> {
    const params = new URLSearchParams();
    appendPricingParams(params, pricing);
    const queryString = params.size ? `?${params.toString()}` : "";
    // The UUID fixes card identity. Only use the shared anonymous edge entry
    // once both pricing dimensions are explicit; otherwise retain the
    // authenticated fallback that can resolve account pricing.
    const get =
      pricing?.market && pricing.currency ? fetch.publicGet : fetch.get;
    const card = (await get(
      `${API_ROUTES.MAGIC_CARDS.GET_BY_ID(dataId)}${queryString}`,
      null
    )) as GameCard;
    if (card.game !== expectedGame) {
      throw new Error(
        `Card ${dataId} returned game ${card.game || "<missing>"}; expected ${expectedGame}`
      );
    }
    return card;
  }
};
